import { Compass, PenTool, Code2, Rocket, TrendingUp, ArrowUpRight } from "lucide-react";
import { Reveal, SectionBadge } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/lib/i18n";

const ICONS = [Compass, PenTool, Code2, Rocket, TrendingUp];

export default function Services() {
  const { t } = useLang();
  const steps = t("services.steps");

  return (
    <section id="services" data-testid="services-section" className="border-t border-[#262930] bg-[#0E1013] py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <SectionBadge label={t("services.badge")} testId="services-badge" />
          <h2 className="font-display mt-5 max-w-2xl text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug text-[#EAE6E1]">
            {t("services.heading")}
          </h2>
          <p className="mt-4 max-w-xl text-base text-[#9E9E9E] leading-relaxed">
            {t("services.sub")}
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
          {steps.map((step, i) => {
            const Icon = ICONS[i];
            return (
              <Reveal key={step.num} delay={i * 0.08}>
                <div
                  data-testid={`service-step-${i}`}
                  className="group flex h-full flex-col rounded-xl border border-[#262930] bg-[#121418] p-4 md:p-7 transition-colors duration-200 hover:border-[#3A3F4B] hover:bg-[#181A20]"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-tech text-[8px] md:text-[10px] uppercase tracking-[0.15em] md:tracking-[0.2em] text-[#686D76]">
                      {t("services.stageLabel")} {step.num}
                    </span>
                    <Icon className="h-4 w-4 md:h-5 md:w-5 shrink-0 text-[#8C9A86]" />
                  </div>
                  <h3 className="font-display mt-3 md:mt-5 text-sm md:text-xl font-semibold tracking-tight leading-snug text-[#EAE6E1]">
                    {step.title}
                  </h3>
                  <ul className="mt-3 md:mt-5 space-y-2 md:space-y-2.5 border-t border-[#262930] pt-3 md:pt-5">
                    {step.items.map((item) => (
                      <li key={item} className="flex items-start gap-1.5 md:gap-2.5 text-[10px] md:text-sm leading-snug text-[#9E9E9E]">
                        <span className="mt-1 md:mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#C87A5B]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            );
          })}

          <Reveal delay={0.4}>
            <button
              data-testid="services-cta-card"
              onClick={() => scrollToId("contact")}
              className="group flex h-full w-full flex-col justify-between rounded-xl border border-[#8C9A86]/40 bg-[#8C9A86]/5 p-4 md:p-7 text-left transition-colors duration-200 hover:bg-[#8C9A86]/10"
            >
              <div>
                <span className="font-tech text-[8px] md:text-[10px] uppercase tracking-[0.15em] md:tracking-[0.2em] text-[#8C9A86]">
                  {t("services.ventureLabel")}
                </span>
                <h3 className="font-display mt-3 md:mt-5 text-sm md:text-xl font-semibold tracking-tight leading-snug text-[#EAE6E1]">
                  {t("services.ctaTitle")}
                </h3>
                <p className="mt-2 md:mt-3 text-[10px] md:text-sm leading-relaxed text-[#9E9E9E]">
                  {t("services.ctaBody")}
                </p>
              </div>
              <span className="mt-4 md:mt-8 inline-flex items-center gap-1.5 md:gap-2 text-[10px] md:text-sm font-semibold text-[#8C9A86]">
                {t("services.ctaLink")}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
